import React from 'react';
import { useSelector } from 'react-redux';
import PermMediaRoundedIcon from '@mui/icons-material/PermMediaRounded';
import useTimeline from '@src/hooks/useTimeline';
import useSourceWaveform from '@src/hooks/useSourceWaveform';
import VirtualizedWaveform from '@src/new-components/Timeline/Waveform/Virtualized';
import { getSourceUrl } from '@src/services/project';
import { readMediaFile } from '@src/services/mediaFile';
import { frameWithinAt } from '@src/services/mediaExtractor';
import { viewTiles } from '@src/services/chunkPlan';
import { THUMB_STATUS, TRACK_HEIGHT } from '@src/constants/timeline';
import { COLOR } from '@src/styles/color';
import useExactFrames from './useExactFrames';
import useVideoThumbnails from './useVideoThumbnails';
import {
  StyledFilmstrip,
  StyledFrame,
  StyledFrameBand,
  StyledLane,
  StyledMediaClip,
  StyledMediaIcon,
  StyledSkeleton,
  StyledWaveBand,
  StyledWaveSkeleton,
} from './index.style';

// Media lane is wide, so a richer waveform. Peaks scale with duration but are
// capped so long videos stay light; videos past the duration cap are skipped.
// A tile's target is one of the strip's own moments, so the frame for it is
// either there or it is not — this only absorbs floating-point noise.
const TARGET_EPSILON_SEC = 0.001;

const MEDIA_WAVEFORM_OPTS = {
  peaksPerSecond: 12,
  minPeaks: 200,
  maxPeaks: 1600,
  // Skip very long videos — decoding a whole one uses too much memory. The
  // download size is capped separately by MAX_WAVEFORM_BYTES.
  maxDurationSec: 91 * 60,
  // Read the bytes the filmstrip is already streaming instead of fetching the
  // file a second time. Module-level and stable, so the hook does not restart.
  getBytes: readMediaFile,
};

/**
 * Media (video) track — the source video as one read-only clip: a band of
 * frames over its audio waveform. Frames fill in as they extract (skeleton
 * meanwhile), falling back to an icon if they can't; the waveform band stays
 * empty if the audio can't be decoded.
 *
 * The track is read-only, so it carries no gesture of its own: pressing it
 * scrubs the playhead via the timeline's surface seek (see DubbingTimeline),
 * exactly like the ruler and the other lanes' empty space.
 */
const MediaTrack = () => {
  const { durationSec, secToPx, pxPerSec, visibleRange } = useTimeline();
  const source = useSelector((state) => state.project.source);
  const videoUrl = getSourceUrl(source);
  // The loader is told the scale rather than the tile span: a tile's width comes
  // from the video's shape, which the hook learns before this does. The scale
  // itself, not `secToPx(1)` — that carries the lane's left inset, which at Fit
  // is several times the scale and had the loader fetching for tiles six times
  // finer than the ones on screen.
  const view = {
    fromSec: visibleRange.fromSec,
    toSec: visibleRange.toSec,
    pxPerSec,
  };
  const {
    status,
    thumbnails,
    times,
    aspect,
    videoDuration,
    keyTimes = [],
    gopSec = 0,
    gridSec = 0,
    // The whole grid was already cached, so tiles aim at it rather than at
    // keyframes — the one thing that would otherwise still need fetching.
    gridComplete = false,
  } = useVideoThumbnails(videoUrl, view);
  // Prefer the backend-generated waveform (a cheap peaks fetch); fall back to
  // decoding the video in-browser only while that isn't ready. The decode, when
  // it runs, reads the same bytes the filmstrip is already streaming.
  const { status: waveStatus, peaks } = useSourceWaveform(
    source,
    MEDIA_WAVEFORM_OPTS,
  );

  // The clip spans the REAL video length, not the whole timeline — so when the
  // voiceover extends past the video (e.g. blocks appended at the end), the
  // media track ends where the video does instead of stretching frames over
  // empty time. Falls back to the timeline duration until metadata loads.
  const clipDuration = videoDuration || durationSec;
  const left = secToPx(0);
  const width = Math.max(secToPx(clipDuration) - left, 0);
  const tileWidth = TRACK_HEIGHT * aspect;
  // Straight off the scale, the same expression the loader uses — a tile span
  // that disagrees with the loader's by even a rounding is a tile waiting on a
  // frame nobody fetched.
  const tileSpanSec = pxPerSec > 0 ? tileWidth / pxPerSec : 0;
  // A strip frame sits exactly on its tile's target, so only floating-point
  // noise has to be absorbed. The <video> fallback publishes no grid — its
  // frames land wherever each seek settled — so there a tile takes the nearest
  // frame within half its own span instead of an exact moment it would never
  // find.
  const targetTolerance = gridSec > 0 ? TARGET_EPSILON_SEC : tileSpanSec / 2;

  // Below the strip's grid a tile stands for a moment the strip has no frame
  // for. Decode what actually plays there — from the chunk the loader is
  // fetching for those same tiles anyway.
  const exact = useExactFrames({
    videoUrl,
    fromSec: visibleRange.fromSec,
    toSec: visibleRange.toSec,
    tileSpanSec,
    stripSpacingSec: gridSec,
  });

  if (!videoUrl) return <StyledLane />;

  // Tile the clip with frames at the video's aspect (downscaled → sharp at any
  // zoom). Each tile shows the frame at its position once extracted, else a
  // skeleton — so the strip fills in from the start. Only visible tiles mount.
  const renderFilmstrip = () => {
    // The same tiles the loader is fetching for, from the same helper: each
    // carries the one frame it will ever show, decided from the zoom and the
    // video's keyframes rather than from whatever has arrived. Showing the
    // nearest frame instead means every tile changes picture each time a closer
    // one lands — at Fit zoom the whole strip flickers through the opening
    // seconds before settling. A skeleton until the right frame is here, and
    // then it stays.
    const tiles = viewTiles({
      fromSec: visibleRange.fromSec,
      toSec: visibleRange.toSec,
      durationSec: clipDuration,
      tileSpanSec,
      keyTimes,
      gridSec,
      gopSec,
      gridComplete,
    }).map(({ index: i, tileSec, target }) => {
      const style = { left: i * tileWidth, width: tileWidth };
      const frameIndex = frameWithinAt(times, target, targetTolerance);
      // A tile finer than the grid has no strip frame of its own; the exact
      // frame for its own moment is what fills it.
      const exactIndex = frameWithinAt(exact.times, tileSec, tileSpanSec / 2);
      const frameUrl =
        exactIndex >= 0
          ? exact.urls[exactIndex]
          : (frameIndex >= 0 && thumbnails[frameIndex]) || null;
      return frameUrl ? (
        <StyledFrame
          key={i}
          style={{ ...style, backgroundImage: `url(${frameUrl})` }}
        />
      ) : (
        <StyledSkeleton key={i} style={style} />
      );
    });
    return <StyledFilmstrip>{tiles}</StyledFilmstrip>;
  };

  return (
    <StyledLane>
      <StyledMediaClip style={{ left, width }}>
        <StyledFrameBand>
          {status === THUMB_STATUS.FAILED ? (
            <StyledMediaIcon>
              <PermMediaRoundedIcon />
            </StyledMediaIcon>
          ) : (
            renderFilmstrip()
          )}
        </StyledFrameBand>
        <StyledWaveBand>
          {waveStatus === THUMB_STATUS.LOADING && <StyledWaveSkeleton />}
          {/* Virtualized: the BE peaks are ~50/second — a long video is far
              too many points for one whole-clip path (it froze the timeline
              on weak machines). Only the visible window is drawn, reduced to
              about one point per pixel. */}
          {waveStatus === THUMB_STATUS.READY && (
            <VirtualizedWaveform
              peaks={peaks}
              durationSec={clipDuration}
              fromSec={visibleRange.fromSec}
              toSec={visibleRange.toSec}
              pxPerSec={pxPerSec}
              color={COLOR.orangeDark[80]}
            />
          )}
        </StyledWaveBand>
      </StyledMediaClip>
    </StyledLane>
  );
};

// Memoized — takes no props, so a parent re-render doesn't cascade into it.
export default React.memo(MediaTrack);
